import React, { useEffect, useCallback } from 'react';
import { compose } from 'redux';
import { defineMessages, useIntl } from 'react-intl';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import withObjectBrowser from '@plone/volto/components/manage/Sidebar/ObjectBrowser';
import { Grid } from 'semantic-ui-react';
import { TextWidget, EditTextBlock } from '@plone/volto/components';
import { ImageFromUrl } from '@package/components';

import navTreeSVG from '@plone/volto/icons/nav.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

const messages = defineMessages({
  title: {
    id: 'fab-title',
    defaultMessage: 'Titolo',
  },
  text: {
    id: 'fab-text',
    defaultMessage: 'Testo del FAB',
  },
  linkUrl_label: {
    id: 'fab-linkUrl_label',
    defaultMessage: 'Label del bottone',
  },
  image: {
    id: 'fab-image',
    defaultMessage: 'Immagine',
  },
  image_description: {
    id: 'fab-image_description',
    defaultMessage: "Seleziona un'immagine o incolla un link esterno.",
  },
  image_title: {
    id: 'fab-image_title',
    defaultMessage: 'Immagine rappresentativa del FAB.',
  },
  linkUrl: {
    id: 'fab-linkUrl',
    defaultMessage: 'URL del bottone',
  },
  linkUrl_description: {
    id: 'slimheader-linkUrl_description',
    defaultMessage:
      'Digita un URL esterno oppure selezionane uno interno cliccando sul pulsante destro.',
  },
});

const FabOverlayFieldsWidget = ({
  id,
  fabItem,
  onChange,
  openObjectBrowser,
}) => {
  const intl = useIntl();

  const preventClick = useCallback((e) => {
    e.preventDefault();
  }, []);

  const preventEnter = useCallback((e) => {
    if (e.code === 'Enter') {
      preventClick(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const form = document.querySelector('form.ui.form');
    const inputs = document.querySelectorAll('form.ui.form input');

    if (form) {
      form.addEventListener('click', preventClick);
    }

    inputs.forEach((input) => {
      input.addEventListener('keypress', preventEnter);
    });

    return () => {
      if (form) {
        form.removeEventListener('click', preventClick);
      }
      inputs.forEach((input) => {
        input.removeEventListener('keypress', preventEnter);
      });
    };
  }, [preventClick, preventEnter]);

  const onChangeFormData = (id, value) => {
    onChange({ ...fabItem, [id]: value });
  };

  const url = fabItem?.linkUrl?.[0]?.['@id'] ?? fabItem?.linkUrl ?? null;
  const image = fabItem?.image?.[0]?.['@id'] ?? fabItem?.image ?? null;

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width="12">
          {/* TITOLO */}
          <TextWidget
            id={`${id}-title`}
            title={intl.formatMessage(messages.title)}
            description=""
            required={true}
            value={fabItem?.title}
            onChange={(id, value) => onChangeFormData('title', value)}
          />
        </Grid.Column>
        {/* TESTO */}
        <Grid.Column width="4" className="inline field">
          <div className="wrapper label-textarea">
            <label htmlFor="textarea-description">
              {intl.formatMessage(messages.text)}
            </label>
          </div>
        </Grid.Column>
        <Grid.Column width="8">
          <EditTextBlock
            detached={true}
            data={fabItem?.description ? fabItem.description : {}}
            onChangeBlock={(id, value) =>
              onChangeFormData('description', value)
            }
          />
        </Grid.Column>
        <Grid.Column width="12" className="fab-image-input">
          {/* IMMAGINE */}
          <TextWidget
            id={`${id}-image`}
            title={intl.formatMessage(messages.image)}
            description={intl.formatMessage(messages.image_description)}
            required={false}
            value={image ? flattenToAppURL(image) : null}
            icon={image ? clearSVG : navTreeSVG}
            iconAction={
              image
                ? () => {
                    onChangeFormData('image', null);
                  }
                : () =>
                    openObjectBrowser({
                      mode: 'image',
                      onSelectItem: (url, item) => {
                        onChangeFormData('image', [item]);
                      },
                    })
            }
            onChange={(id, value) => onChangeFormData('image', value)}
          />{' '}
        </Grid.Column>
        <Grid.Column width="12">
          <div className="fab-image-preview">
            {image && (
              <ImageFromUrl
                url={image}
                title={intl.formatMessage(messages.image_title)}
                size="mini"
              />
            )}
          </div>
        </Grid.Column>
        {/* LABEL DEL BOTTONE */}
        <Grid.Column width="12">
          <TextWidget
            id={`${id}-labelUrl`}
            title={intl.formatMessage(messages.linkUrl_label)}
            description=""
            required={false}
            value={fabItem?.labelUrl}
            onChange={(id, value) => onChangeFormData('labelUrl', value)}
          />
        </Grid.Column>
        {/* LINK BOTTONE */}
        <Grid.Column width="12">
          <TextWidget
            id={`${id}-linkUrl`}
            title={intl.formatMessage(messages.linkUrl)}
            description={intl.formatMessage(messages.linkUrl_description)}
            required={true}
            value={url ? flattenToAppURL(url) : null}
            icon={url ? clearSVG : navTreeSVG}
            iconAction={
              url
                ? () => {
                    onChangeFormData('linkUrl', null);
                  }
                : () =>
                    openObjectBrowser({
                      mode: 'link',
                      onSelectItem: (url, item) => {
                        onChangeFormData('linkUrl', [item]);
                      },
                    })
            }
            onChange={(id, value) => onChangeFormData('linkUrl', value)}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default React.memo(compose(withObjectBrowser)(FabOverlayFieldsWidget));
